import React, { useState, useEffect, useCallback } from 'react';
import { Paper, Box, Typography, List, ListItem, ListItemText, ListItemSecondaryAction, IconButton, Chip, CircularProgress, Alert, Avatar, Tooltip } from '@mui/material';
import { Download, Visibility, PictureAsPdf, Image, Description } from '@mui/icons-material';
import apiClient from '../../config/axios';
import dayjs from 'dayjs';
import 'dayjs/locale/it';

dayjs.locale('it');

const UserDetailsFiles = ({ user, files: initialFiles }) => {
  const [files, setFiles] = useState(initialFiles || []);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [fileError, setFileError] = useState('');
  const [filePreviews, setFilePreviews] = useState({});

  const isImageFile = (fileName) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif'].includes(extension);
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (extension === 'pdf') return <PictureAsPdf color="error" />;
    if (isImageFile(fileName)) return <Image color="primary" />;
    return <Description color="action" />;
  };

  const truncateFileName = (fileName, maxLength = 30) => {
    if (fileName.length <= maxLength) return fileName;
    const extension = fileName.split('.').pop();
    const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
    const truncatedName = nameWithoutExt.substring(0, maxLength - extension.length - 4) + '...';
    return `${truncatedName}.${extension}`;
  };

  const getFileTypeLabel = (fileName) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (extension === 'pdf') return 'PDF';
    if (extension === 'jpg' || extension === 'jpeg') return 'JPG';
    if (extension === 'png') return 'PNG';
    return extension?.toUpperCase() || 'File';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const loadUserFiles = useCallback(async () => {
    if (!user?._id) return;

    setLoadingFiles(true);
    setFileError('');

    try {
      const response = await apiClient.get(`/api/files/user/${user._id}`);
      const userFiles = response.data.data || [];
      setFiles(userFiles);

      // Genera anteprime per le immagini
      const newPreviews = {};
      for (const file of userFiles) {
        if (isImageFile(file.fileName)) {
          try {
            const previewResponse = await apiClient.get(`/api/files/view/${encodeURIComponent(file.key)}`, {
              responseType: 'blob',
            });
            const previewUrl = URL.createObjectURL(previewResponse.data);
            newPreviews[file.key] = previewUrl;
          } catch (error) {
            console.error('Error generating preview for:', file.fileName, error);
          }
        }
      }
      setFilePreviews(newPreviews);
    } catch (error) {
      console.error('Error loading user files:', error);
      setFileError('Errore nel caricamento dei file');
    } finally {
      setLoadingFiles(false);
    }
  }, [user?._id]);

  const handleDownloadFile = async (fileKey, fileName) => {
    try {
      const response = await apiClient.get(`/api/files/download/${encodeURIComponent(fileKey)}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      setFileError('Errore nel download del file');
    }
  };

  const handleViewFile = async (fileKey, fileName) => {
    try {
      const response = await apiClient.get(`/api/files/view/${encodeURIComponent(fileKey)}`, {
        responseType: 'blob',
      });

      // Determina il content type basandosi sull'estensione del file
      const extension = fileName.split('.').pop()?.toLowerCase();
      let contentType = 'application/octet-stream';

      if (extension === 'pdf') {
        contentType = 'application/pdf';
      } else if (['jpg', 'jpeg'].includes(extension)) {
        contentType = 'image/jpeg';
      } else if (extension === 'png') {
        contentType = 'image/png';
      } else if (extension === 'gif') {
        contentType = 'image/gif';
      }

      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error viewing file:', error);
      setFileError('Errore nella visualizzazione del file');
    }
  };

  useEffect(() => {
    const processFiles = async () => {
      if (initialFiles?.length === 0) {
        await loadUserFiles();
      } else if (initialFiles) {
        setFiles(initialFiles);
      }
    };

    processFiles();
  }, [initialFiles, loadUserFiles]);

  // Cleanup delle anteprime quando il componente si smonta
  useEffect(() => {
    return () => {
      Object.values(filePreviews).forEach((url) => {
        URL.revokeObjectURL(url);
      });
    };
  }, [filePreviews]);

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        File Associati
      </Typography>

      {fileError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFileError('')}>
          {fileError}
        </Alert>
      )}

      {loadingFiles ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress />
        </Box>
      ) : files.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Nessun file associato a questo utente
          </Typography>
        </Box>
      ) : (
        <Box sx={{ p: 2 }}>
          <List dense>
            {files.map((file) => (
              <ListItem key={file.key} sx={{ px: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                  {isImageFile(file.fileName) && filePreviews[file.key] ? (
                    <Avatar
                      src={filePreviews[file.key]}
                      variant="rounded"
                      sx={{ width: 40, height: 40, mr: 1 }}
                    />
                  ) : (
                    getFileIcon(file.fileName)
                  )}
                </Box>
                <ListItemText
                  primary={
                    <Tooltip title={file.fileName} placement="top">
                      <span>{truncateFileName(file.fileName)}</span>
                    </Tooltip>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label={getFileTypeLabel(file.fileName)} size="small" variant="outlined" />
                      <Typography variant="caption" color="text.secondary">
                        {formatFileSize(file.size)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {dayjs(file.lastModified).format('DD-MMM-YY')}
                      </Typography>
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleViewFile(file.key, file.fileName)}
                    title="Visualizza"
                  >
                    <Visibility />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleDownloadFile(file.key, file.fileName)}
                    title="Scarica"
                  >
                    <Download />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Paper>
  );
};

export default UserDetailsFiles;

